import React, { useState } from 'react';
import Fetching from './fetching';
import { getEmployeesWithActiveContract } from "../services/api_calendar";
import ListBoxQueryMultiSelectEmployeesActiveContract from './list_box_query_employee_active_contract';
import { AcademicCapIcon, UserGroupIcon } from '@heroicons/react/24/outline';

export default function AlertForm({
  titleError,
  setTitleError,
  messageError,
  setMessageError,
  recipientError,
  selectedOption,
  selectedUnits,
  selectedEmployees,
  units,
  filter,
  searchQuery,
  isLoadingOu,
  handleOptionChange,
  handleUnitSelection,
  handleEmployeeSelection,
  handleCheckboxChange,
  handleCreateAlert,
  token,
  titleRef,
  messageRef,
  recipientRef,
  attachments,
  setAttachments,
  canManageAlertsAll,
}) {
  const [attachmentError, setAttachmentError] = useState(null);
  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
  const MAX_TOTAL_ATTACHMENT_SIZE = 25 * 1024 * 1024;

  const getFilterValue = () => {
    if (filter === 'active') return 'contract_active';
    if (filter === 'inactive') return 'contract_not_active';
    if (filter === 'all') return 'all';
    return null;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const addAttachments = (selectedFiles) => {
    const files = Array.from(selectedFiles || []);
    if (!files.length) {
      return;
    }
    const newFiles = files.filter((file) => {
      return !attachments.some((attachment) =>
        attachment.name === file.name &&
        attachment.size === file.size &&
        attachment.lastModified === file.lastModified
      );
    });
    const updatedAttachments = [
      ...attachments,
      ...newFiles
    ];
    const totalSize = updatedAttachments.reduce(
      (total, file) => total + file.size,
      0
    );
    if (totalSize > MAX_TOTAL_ATTACHMENT_SIZE) {
      setAttachmentError('La dimensione complessiva degli allegati non può superare 25 MB.');
      return;
    }
    setAttachmentError(null);
    setAttachments(updatedAttachments);
  };

  const handleAttachmentChange = (event) => {
    addAttachments(event.target.files);
    event.target.value = '';
  };

  const handleAttachmentDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingAttachments(true);
  };

  const handleAttachmentDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingAttachments(false);
  };

  const handleAttachmentDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingAttachments(false);
    addAttachments(event.dataTransfer.files);
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((currentAttachments) =>
      currentAttachments.filter((_, currentIndex) => currentIndex !== index)
    );
    setAttachmentError(null);
  };

  const totalAttachmentSize = attachments.reduce(
    (total, file) => total + file.size,
    0
  );

  return (
    <form onSubmit={handleCreateAlert}>
      <div className='p-2'>
        <div ref={titleRef}>
          <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900">
            Titolo
          </label>
          <div className="mt-2">
            <input
              type="text"
              name="title"
              id="title"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-300 sm:text-sm sm:leading-6"
              placeholder="Titolo..."
              onChange={() => setTitleError(null)}
            />
            {titleError && <p className="text-red-500 text-xs mt-1">{titleError}</p>}
          </div>
        </div>
        <div ref={messageRef} className="mt-4">
          <label htmlFor="comment" className="block text-sm font-medium leading-6 text-gray-900">
            Messaggio
          </label>
          <div className="mt-2">
            <textarea
              rows={4}
              name="comment"
              id="comment"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-300 sm:text-sm sm:leading-6"
              placeholder="Scrivi il tuo messaggio..."
              defaultValue={''}
              onChange={() => setMessageError(null)}
            />
            {messageError && <p className="text-red-500 text-xs mt-1">{messageError}</p>}
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Allegati
          </label>
          <div className="mt-2">
            <label
              htmlFor="attachments"
              onDragOver={handleAttachmentDragOver}
              onDragEnter={handleAttachmentDragOver}
              onDragLeave={handleAttachmentDragLeave}
              onDrop={handleAttachmentDrop}
              className={`flex flex-col items-center justify-center w-full min-h-[120px] px-4 py-5 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isDraggingAttachments
                  ? 'border-indigo-500 bg-indigo-100'
                  : 'border-indigo-300 bg-indigo-50/50 hover:bg-indigo-50 hover:border-indigo-400'
              }`}
            >
              <div className="flex flex-col items-center justify-center text-center pointer-events-none">
                <svg
                  className={`w-8 h-8 mb-2 transition-colors ${isDraggingAttachments ? 'text-indigo-600' : 'text-indigo-400'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <p className="text-sm text-indigo-600">
                  {isDraggingAttachments ? (
                    <span className="font-semibold">Rilascia qui i file</span>
                  ) : (
                    <>
                      <span className="font-semibold">Clicca per caricare uno o più file</span>
                      <span className="hidden sm:inline"> o trascinali qui</span>
                    </>
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Dimensione massima complessiva: 25 MB
                </p>
              </div>
              <input
                id="attachments"
                type="file"
                multiple
                className="hidden"
                onChange={handleAttachmentChange}
              />
            </label>
            {attachmentError && (
              <p className="text-red-500 text-xs mt-2">
                {attachmentError}
              </p>
            )}
          </div>
          {attachments.length > 0 && (
            <div className="mt-3 border border-gray-200 rounded-md overflow-hidden">
              <div className="flex items-center justify-between bg-gray-50 px-3 py-2">
                <span className="text-xs font-semibold text-gray-700">
                  Allegati: {attachments.length}
                </span>
                <span className="text-xs font-medium text-gray-500">
                  {formatFileSize(totalAttachmentSize)} / 25 MB
                </span>
              </div>
              <div className="divide-y divide-gray-200">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="flex items-center justify-between px-3 py-2 bg-white"
                  >
                    <div className="flex items-center min-w-0">
                      <svg
                        className="w-5 h-5 mr-2 text-indigo-500 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828L18 9.828a4 4 0 10-5.656-5.656L5.758 10.758a6 6 0 108.484 8.484L20.5 13"
                        />
                      </svg>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(index)}
                      className="ml-4 text-xs font-semibold text-red-600 hover:text-red-500"
                    >
                      Rimuovi
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-4" ref={recipientRef}>
          <label className="block text-sm font-medium leading-6 text-gray-900">Invia a</label>
          <div className="mt-2 flex flex-col">
            {canManageAlertsAll && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedOption === 'all'}
                  onChange={() => handleOptionChange('all')}
                  className="rounded text-indigo-600 focus:ring-indigo-600"
                />
                <label htmlFor="all" className="block text-sm font-medium ml-2 leading-6 text-gray-900">Tutti</label>
              </div>
            )}
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                checked={selectedOption === 'unit'}
                onChange={() => handleOptionChange('unit')}
                className="rounded text-indigo-600 focus:ring-indigo-600"
              />
              <label htmlFor="unit" className="block text-sm font-medium ml-2 leading-6 text-gray-900">Seleziona Unità Organizzative</label>
            </div>
            <div className="flex items-center mt-2">
              <input
                type="checkbox"
                checked={selectedOption === 'employee'}
                onChange={() => handleOptionChange('employee')}
                className="rounded text-indigo-600 focus:ring-indigo-600"
              />
              <label htmlFor="employee" className="block text-sm font-medium ml-2 leading-6 text-gray-900">Seleziona Dipendenti</label>
            </div>
            {recipientError && <p className="text-red-500 text-xs mt-1">{recipientError}</p>}
          </div>
        </div>

        {selectedOption === 'unit' && (
          <div className="mt-4 max-h-80 overflow-y-auto border rounded-lg shadow-md">
            <h3 className='block sticky top-0 text-sm font-semibold leading-6 text-indigo-700 uppercase bg-indigo-100 p-3 rounded-t-lg'>
              <AcademicCapIcon className='inline-block shrink-0 h-5 w-5 me-2' />
              Seleziona Unità Organizzative
            </h3>
            <div className='bg-white divide-y divide-gray-200'>
              {isLoadingOu ? (
                <Fetching />
              ) : (
                units
                  .slice()
                  .filter((unit) => !selectedUnits.some((selectedUnit) => selectedUnit.id === unit.id))
                  .map((unit) => (
                    <div key={unit.id} className="col-span-3 block text-sm font-medium leading-6 text-gray-600 cursor-pointer border-t">
                      <div
                        className="text-sm font-medium leading-6 text-gray-600 cursor-pointer hover:bg-indigo-50 p-2 px-4 justify-between items-center grid grid-cols-5"
                        onClick={() => handleUnitSelection(unit)}
                      >
                        <div className='col-span-3'>{unit.name}</div>
                        <div className='col-span-1 text-xs'>GESTORI: {unit.managers}</div>
                        <div className='col-span-1 text-xs'>DIPENDENTI: {unit.resources}</div>
                      </div>
                    </div>
                  )))}
            </div>
          </div>
        )}

        {selectedUnits.length > 0 && selectedOption === 'unit' && (
          <div className="border mt-6 rounded-lg shadow-md">
            <h3 className='block text-sm font-bold leading-6 text-indigo-800 uppercase bg-indigo-200 p-3 rounded-t-lg'>
              Destinatari: {selectedUnits.length} unità
            </h3>
            <div className='block text-xs font-medium ml-2 leading-6 text-gray-700 mb-2'>
              Puoi rimuovere le Unità dalla lista dei destinatari cliccando sul nome
            </div>
            <div className='max-h-80 overflow-y-auto'>
              {selectedUnits.map((unit) => (
                <button
                  key={unit.id}
                  className="block w-full text-left grid grid-cols-5 hover:bg-indigo-50"
                  onClick={() => handleUnitSelection(unit)}
                >
                  <div className='col-span-3 block text-sm font-medium leading-6 text-gray-600 cursor-pointer p-2 px-4 border-t'>
                    {unit.name}
                  </div>
                  <div className='col-span-1 block text-xs font-medium leading-6 text-gray-600 cursor-pointer p-2 border-t'>
                    GESTORI: {unit.managers}
                  </div>
                  <div className='col-span-1 block text-xs font-medium leading-6 text-gray-600 cursor-pointer p-2 border-t'>
                    DIPENDENTI: {unit.resources}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {selectedOption === 'employee' && (
          <div className='grid-cols-1 sm:grid grid-cols-3 gap-4 mt-2'>
            <div className="mt-4 col-span-1 border border-gray-200 rounded-lg shadow-md">
              <h3 className='block sticky top-0 text-sm z-10 font-semibold leading-6 bg-indigo-100 text-indigo-700 uppercase p-3 rounded-t-md'>
                <UserGroupIcon className='inline-block shrink-0 h-5 w-5 me-2' />
                Seleziona Dipendenti
              </h3>
              <div className='z-10 px-3 pt-2'>
                <p className='block text-xs font-medium ml-2 leading-6 text-gray-900 uppercase tracking-wider'>
                  Visualizza:
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-900">
                    <input
                      type="checkbox"
                      value="active"
                      checked={filter === 'active'}
                      onChange={handleCheckboxChange}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span>Contratti Attivi</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-900">
                    <input
                      type="checkbox"
                      value="inactive"
                      checked={filter === 'inactive'}
                      onChange={handleCheckboxChange}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span>Contratti Non Attivi</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm font-medium text-gray-900">
                    <input
                      type="checkbox"
                      value="all"
                      checked={filter === 'all'}
                      onChange={handleCheckboxChange}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                    <span>Tutti</span>
                  </label>
                </div>
                <div className='col-span-1 pt-4 pb-2 h-60'>
                  <ListBoxQueryMultiSelectEmployeesActiveContract
                    token={token}
                    selectedEmployees={selectedEmployees}
                    onChange={handleEmployeeSelection}
                    employeeRolePairs={[]}
                    placeholder="Digita per cercare il dipendente"
                    wrapperClassName="h-60"
                    filterBy={getFilterValue()}
                    pageSize={10}
                    viewEmail={true}
                    multiple={true}
                  />
                </div>
              </div>
            </div>
            {selectedOption === 'employee' && (
              <div className="border col-span-1 sm:col-span-2 mt-4 rounded-lg shadow-md">
                <h3 className='block text-sm font-semibold leading-6 text-indigo-700 uppercase bg-indigo-100 p-3 rounded-t-md'>
                  Destinatari: {selectedEmployees?.length} dipendenti
                </h3>
                <div className='block text-xs font-medium ml-2 leading-6 text-gray-700 mb-2'>
                  Puoi rimuovere i dipendenti dalla lista dei destinatari cliccando sul nome
                </div>
                <div className='max-h-80 overflow-y-auto'>
                  {selectedEmployees?.map((employee) => (
                    <button
                      key={employee.id}
                      className="block w-full text-left"
                      onClick={() =>
                        handleEmployeeSelection(
                          selectedEmployees.filter(
                            selectedEmployee => selectedEmployee.id !== employee.id
                          )
                        )
                      }
                    >
                      <div className='block text-sm font-medium leading-6 text-gray-600 cursor-pointer hover:bg-indigo-50 p-2 px-4 border-t'>
                        {employee.last_name && employee.first_name
                          ? `${employee.last_name} ${employee.first_name}`
                          : employee.cf}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

          </div>
 
        )}


        {selectedOption === 'all' ? (
          <div className="p-2 mt-2 font-semibold text-center text-sm text-gray-700">
            Il messaggio verrà inviato a tutti i dipendenti con accesso app attivo
          </div>
        ) : selectedOption === 'unit' && selectedUnits.length ? (
          <div className="p-2 mt-2 font-semibold text-center text-sm text-gray-700">
            Il messaggio verrà inviato ai dipendenti con accesso app attivo che fanno parte dell'unità selezionata
          </div>
        ) : null}

        <div className="justify-center flex mt-5">
          <button
            type="submit"
            className="border block rounded-md px-3 py-2 mr-2 text-center text-sm font-semibold text-white bg-indigo-600 shadow-md hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
          >
            Invia Messaggio
          </button>
        </div>
      </div>
    </form>
  );
}
